import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, output, signal, untracked } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { NgSelectModule } from '@ng-select/ng-select';
import { SharedModule } from '@shared/shared.module';
import { PlatformChartListItemDto } from '@data/_generated-api/platform-api/models/platform-chart-list-item-dto';
import { PlatformChartType } from '@data/_generated-api/platform-api/models/platform-chart-type';
import { DsQueryFilterOperator } from '@common/result/data-source/enum/ds-query-filter-operator';
import {
  HD_CHART_GAME_OPTIONS,
  HdChartGameId,
  HdChartGameVariantId,
} from '../hd-chart-controls-options';
import { HD_BODYGRAPH_GATES } from '../hd-bodygraph-metadata';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';
import { HdChartLibraryService } from '../../library/hd-chart-library.service';

/**
 * Dialog met de spelkeuze van de chart-omgeving: spelmodus-toggle, spel- en
 * variantkeuze, poortrange- en teken-chart-opties en de play/stop-knoppen.
 * Voorheen de "Spel"-sectie van het settings-panel; de pagina opent de dialog
 * vanuit de game-knop in de shortcut-bar zodat het settings-panel enkel nog
 * echte settings bevat.
 */
@Component({
  selector: 'hd-chart-game-dialog',
  imports: [SharedModule, NgSelectModule],
  templateUrl: './hd-chart-game-dialog.component.html',
  styleUrl: './hd-chart-game-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdChartGameDialogComponent {
  /** Two-way open-state; de pagina opent de dialog vanuit de shortcut-bar. */
  readonly open = model(false);

  readonly gameMode = input(false);
  readonly gameActive = input(false);
  readonly selectedGameId = input<HdChartGameId>('gate-guessing');
  readonly selectedGameVariantId = input<HdChartGameVariantId>('numbers');
  readonly gateRangeSortEnabled = input(false);
  readonly gateRangeStart = input(HD_BODYGRAPH_GATES[0]);
  readonly gateRangeEnd = input(HD_BODYGRAPH_GATES[HD_BODYGRAPH_GATES.length - 1]);
  /** Doel-chart (library-id) voor "Teken je chart"; gekozen uit de eigen bibliotheek. */
  readonly drawChartTargetId = input('');

  // Outputs — namen identiek aan de vroegere settings-panel outputs
  readonly gameModeToggled = output<void>();
  readonly gameSelectionChange = output<HdChartGameId>();
  readonly gameVariantChange = output<HdChartGameVariantId>();
  readonly gateRangeSortToggled = output<void>();
  readonly gateRangeStartChange = output<number>();
  readonly gateRangeEndChange = output<number>();
  readonly drawChartTargetChange = output<string>();
  readonly playClicked = output<void>();
  readonly stopRequested = output<void>();

  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly library = inject(HdChartLibraryService);
  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  /**
   * Standard-charts uit de eigen bibliotheek als doel voor "Teken je chart"
   * (sinds 2026-08-23 de enige chartbron — de ingebouwde presets zijn weg).
   * Geladen telkens de dialog open is mét het teken-spel gekozen, zodat een
   * net opgeslagen chart meteen kiesbaar is.
   */
  protected readonly drawChartTargets = signal<readonly PlatformChartListItemDto[]>([]);
  protected readonly drawChartTargetsLoading = signal(false);

  protected readonly title = computed(() => {
    this.contentLang.translationsLoadedTick();
    return this.transloco.translate<string>(
      'humanDesign.chart.gameDialog.title',
      undefined,
      this.contentLang.effectiveLang(),
    );
  });

  protected readonly gameOptions = computed(() =>
    HD_CHART_GAME_OPTIONS.map(game => ({
      id: game.id,
      label: this.t(game.labelKey),
      variants: game.variants.map(variant => ({ id: variant.id, label: this.t(variant.labelKey) })),
    })),
  );

  protected readonly selectedGameOption = computed(
    () => this.gameOptions().find(option => option.id === this.selectedGameId()) ?? this.gameOptions()[0],
  );
  protected readonly hasGameVariants = computed(() => this.selectedGameOption().variants.length > 0);
  protected readonly showGateRangeControls = computed(
    () => this.selectedGameId() === 'gate-guessing' && this.selectedGameVariantId() === 'memory-keynote',
  );
  protected readonly showDrawChartControls = computed(() => this.selectedGameId() === 'draw-chart');

  constructor() {
    effect(() => {
      if (this.open() && this.showDrawChartControls()) {
        untracked(() => void this.loadDrawChartTargets());
      }
    });
  }

  private async loadDrawChartTargets(): Promise<void> {
    this.drawChartTargetsLoading.set(true);
    try {
      const items = await this.library.queryAllCharts({
        sortOptions: [{ propertyName: 'Name', direction: 'ASC' }],
        filterOptionGroup: {
          logicalOperator: '',
          filterOptionGroups: [],
          filterOptions: [{
            propertyName: 'Type',
            operator: DsQueryFilterOperator.IntEquals,
            value: `${PlatformChartType.Standard}`,
          }],
        },
      });
      this.drawChartTargets.set(items);
      this.applyDefaultDrawChartTarget(items);
    } catch {
      this.drawChartTargets.set([]);
    } finally {
      this.drawChartTargetsLoading.set(false);
    }
  }

  /**
   * Zonder (geldige) keuze valt het teken-doel terug op de chart die als
   * "mijn chart" gemarkeerd is (feedback 2026-08-23): de pagina bewaart de
   * keuze per tab, dus we melden ze als gewone wijziging.
   */
  private applyDefaultDrawChartTarget(items: readonly PlatformChartListItemDto[]): void {
    const current = this.drawChartTargetId();
    if (current && items.some(item => item.id === current)) {
      return;
    }
    const myChart = items.find(item => item.isMyChart);
    if (myChart?.id != null && myChart.id !== current) {
      this.drawChartTargetChange.emit(myChart.id);
    }
  }

  /** Poortrange-opties met "Van {{gate}}"/"Tot {{gate}}"-labels (vertaald). */
  protected readonly gateRangeStartOptions = computed(() =>
    HD_BODYGRAPH_GATES.map(gate => ({ id: gate, label: this.t('humanDesign.chart.controls.prefixes.from', { gate }) })),
  );
  protected readonly gateRangeEndOptions = computed(() =>
    HD_BODYGRAPH_GATES.map(gate => ({ id: gate, label: this.t('humanDesign.chart.controls.prefixes.to', { gate }) })),
  );

  protected t(key: string, params?: Record<string, unknown>): string {
    // Zie hd-chart-settings-panel.t: translationsLoadedTick mee-lezen zodat de
    // template opnieuw evalueert wanneer de override-taal ná de wissel is
    // ingeladen.
    this.contentLang.translationsLoadedTick();
    return this.transloco.translate(key, params, this.lang());
  }

  /** Play sluit de dialog: het spel start en de config is niet meer nodig. */
  protected onPlayClicked(): void {
    this.playClicked.emit();
    this.open.set(false);
  }
}
